import { useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Bike,
  CheckCircle2,
  Banknote,
  MapPinned,
  PackageCheck,
  Phone,
  Store,
} from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { ErrorState, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { formatPrice, loc } from "@/lib/format";
import { branchesQuery, customerOrderQuery } from "@/lib/queries";
import { localized, useStorefront } from "@/lib/storefront";
import { OrderLoyalty } from "@/components/orders/OrderLoyalty";

export const Route = createFileRoute("/order-success")({
  validateSearch: (search: Record<string, unknown>) => ({
    orderId: String(search["orderId"] ?? ""),
  }),
  component: OrderSuccess,
});

function OrderSuccess() {
  const { orderId } = Route.useSearch();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const storefront = useStorefront();
  const { hydrated, isAuthenticated, user } = useAuth();
  const query = useQuery({
    ...customerOrderQuery(user?.id ?? "", orderId),
    enabled: Boolean(orderId && hydrated && isAuthenticated),
  });
  const { data: branches } = useQuery({
    ...branchesQuery(),
    enabled: Boolean(hydrated && isAuthenticated),
  });
  useEffect(() => {
    if (
      !orderId ||
      (hydrated &&
        isAuthenticated &&
        !query.isPending &&
        !query.isError &&
        !query.data)
    ) {
      navigate({ to: "/orders", replace: true });
    }
    if (orderId && hydrated && !isAuthenticated) {
      navigate({ to: "/login", replace: true });
    }
  }, [
    hydrated,
    isAuthenticated,
    navigate,
    orderId,
    query.data,
    query.isError,
    query.isPending,
  ]);

  if (
    !orderId ||
    !hydrated ||
    !isAuthenticated ||
    query.isPending ||
    (!query.isError && !query.data)
  ) {
    return (
      <PageShell size="narrow">
        <div
          className="surface-card mx-auto grid max-w-md place-items-center gap-5 p-10 text-center"
          role="status"
        >
          <span className="relative grid size-20 place-items-center rounded-full bg-accent text-primary">
            <PackageCheck className="size-9" aria-hidden="true" />
            <Spinner className="absolute -bottom-1 -end-1 size-7 rounded-full bg-card p-1.5 shadow-soft" />
          </span>
          <div>
            <h1 className="text-xl font-black">{t("success.loadingTitle")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("success.loadingDescription")}
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (query.isError) {
    return (
      <PageShell size="narrow">
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </PageShell>
    );
  }

  const order = query.data;
  if (!order) return null;
  const branch = branches?.find((candidate) => candidate.id === order.branchId);
  const restaurantName = localized(storefront.tenant.name, lang);
  const branchName = branch
    ? loc(branch.name, lang)
    : t("success.branchPending");
  const contactPhone =
    branch?.phones.find(Boolean) ?? storefront.tenant.supportPhone;
  const promotion = order.promotions[0];
  const paymentLabel = t(
    order.paymentMethod === "online"
      ? "success.onlinePaid"
      : order.type === "delivery"
        ? "success.cashDelivery"
        : "success.cashPickup",
  );

  return (
    <PageShell size="narrow">
      <div className="mx-auto grid max-w-2xl gap-5">
        <section className="motion-success-card surface-card overflow-hidden text-center">
          <div className="pattern-levant grid place-items-center gap-3 px-5 py-8 sm:px-8 sm:py-10">
            <span className="motion-success-icon grid size-16 place-items-center rounded-full bg-success/12 text-success">
              <CheckCircle2 className="size-9" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-bold text-success">
                {t("success.confirmed")}
              </p>
              <h1 className="mt-1 text-2xl font-black sm:text-3xl">
                {t("success.title")}
              </h1>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                {t("success.desc")}
              </p>
            </div>
            <p className="rounded-2xl bg-card px-5 py-3 text-lg font-black text-primary shadow-soft">
              {t("success.orderNumber")} #{order.number}
            </p>
          </div>

          <dl className="grid gap-px border-t border-border bg-border text-start sm:grid-cols-2">
            <ConfirmationRow
              icon={<Store />}
              label={t("success.restaurant")}
              value={restaurantName}
            />
            <ConfirmationRow
              icon={<MapPinned />}
              label={t("success.branch")}
              value={branchName}
            />
            <ConfirmationRow
              icon={order.type === "delivery" ? <Bike /> : <PackageCheck />}
              label={t("success.orderType")}
              value={t(
                order.type === "delivery" ? "menu.delivery" : "menu.pickup",
              )}
            />
            <ConfirmationRow
              icon={<Banknote />}
              label={t("success.payment")}
              value={paymentLabel}
              wide
            />
          </dl>
        </section>

        {promotion ? (
          <div className="rounded-2xl border border-success/25 bg-success/10 px-4 py-3 text-success">
            <p className="font-black">
              {promotion.name[lang] || promotion.name.ar || promotion.name.en}
            </p>
            <p className="mt-1 text-sm">
              {t("success.promotionSaved")}{" "}
              {formatPrice(promotion.savings, lang)}
            </p>
          </div>
        ) : null}

        <OrderLoyalty order={order} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            asChild
            size="lg"
            className={contactPhone ? "" : "sm:col-span-2"}
          >
            <Link to="/orders/$orderId" params={{ orderId: order.id }}>
              {t("success.trackOrder")}
            </Link>
          </Button>
          {contactPhone ? (
            <Button asChild size="lg" variant="outline">
              <a href={`tel:${contactPhone}`}>
                <Phone className="size-4" aria-hidden="true" />
                {t("success.callRestaurant")}
              </a>
            </Button>
          ) : null}
          <Button asChild variant="ghost" className="sm:col-span-2">
            <Link to="/account">{t("success.account")}</Link>
          </Button>
        </div>
      </div>
    </PageShell>
  );
}

function ConfirmationRow({
  icon,
  label,
  value,
  wide = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  wide?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3 bg-card px-4 py-4 sm:px-5 ${wide ? "sm:col-span-2" : ""}`}
    >
      <span className="mt-0.5 text-primary [&_svg]:size-5" aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
        <dd className="mt-1 break-words font-bold">{value}</dd>
      </div>
    </div>
  );
}
